// ─── Navigateur de fichiers du poste (canal « fichiers ») ───
// Protocole Telipso Assistance v2 §2 « Fichiers » : requêtes corrélées par `id`,
// morceaux binaires de 64 Kio (0x10 poste ▸ console, 0x11 console ▸ poste).
// Le canal n'est ouvert qu'à la première ouverture du panneau.

import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowUp, Download, File as FileIcon, Folder, FolderPlus, Pencil, RefreshCw, Trash2, Upload } from 'lucide-react';
import { useChannel } from './useChannel.js';
import { useTableSort } from '../lib/useTableSort.jsx';
import { ClientFichiers, enregistrer, fmtTaille, joindre } from './fichiersClient.js';

const btn = 'inline-flex items-center gap-1 bg-card border border-rule-3 rounded-lg px-2 py-1 text-xs text-fg-2 cursor-pointer hover:text-fg disabled:opacity-50 disabled:cursor-default';
const iconBtn = 'bg-transparent border-none p-1 rounded text-muted cursor-pointer hover:text-fg';

const fmtDate = (ms) => (ms ? new Date(ms).toLocaleString('fr-CA', { dateStyle: 'short', timeStyle: 'short' }) : '');

export function Files({ sessionId, visible }) {
  const [demarre, setDemarre] = useState(false);
  const [chemin, setChemin] = useState('');
  const [saisie, setSaisie] = useState('');
  const [parent, setParent] = useState('');
  const [entrees, setEntrees] = useState([]);
  const [racines, setRacines] = useState([]);
  const [erreur, setErreur] = useState('');
  const [chargement, setChargement] = useState(false);
  const [transferts, setTransferts] = useState([]);
  const [survol, setSurvol] = useState(false);
  const { toggleSort, sortRows, headerProps, arrow } = useTableSort('nom');

  const choix = useRef(null);
  const canalRef = useRef(null);
  const clientRef = useRef(null);
  if (!clientRef.current) clientRef.current = new ClientFichiers(() => canalRef.current);
  const client = clientRef.current;
  const cheminRef = useRef('');
  const entreesRef = useRef([]);
  cheminRef.current = chemin;
  entreesRef.current = entrees;

  useEffect(() => {
    if (visible) setDemarre(true);
  }, [visible]);

  const majTransfert = (id, maj) => setTransferts((l) => l.map((t) => (t.id === id ? { ...t, ...maj } : t)));

  const canal = useChannel(
    sessionId,
    'fichiers',
    (e) => client.traiter(e),
    () => {
      // Ouverture : on demande les racines, puis on ouvre la première (Bureau de l'usager, sinon un lecteur).
      client.demander({ t: 'racines' }).then(
        (m) => {
          const l = m.chemins || [];
          setRacines(l);
          if (l[0]) ouvrir(l[0]);
        },
        (err) => setErreur(err.message),
      );
    },
    { enabled: demarre },
  );
  canalRef.current = canal;

  // Fermeture du canal : les requêtes en attente ne reviendront pas.
  useEffect(() => {
    if (canal.etat !== 'ferme') return;
    client.rejeterTout(canal.raison || 'Canal fermé');
  }, [canal.etat, canal.raison, client]);

  const demander = useCallback((message) => client.demander(message), [client]);

  async function ouvrir(dossier) {
    setErreur('');
    setChargement(true);
    try {
      const r = await demander({ t: 'lister', chemin: dossier });
      setChemin(r.chemin);
      setSaisie(r.chemin);
      setParent(r.parent || '');
      setEntrees(r.entrees || []);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setChargement(false);
    }
  }

  async function action(message) {
    setErreur('');
    try {
      await demander(message);
      await ouvrir(cheminRef.current);
    } catch (e) {
      setErreur(e.message);
    }
  }

  // Observateur commun aux deux sens : alimente la liste des transferts.
  const observateur = {
    debut: (id, nom, total, sens) => setTransferts((l) => [...l, { id, nom, sens, fait: 0, total }]),
    progres: (id, fait) => majTransfert(id, { fait }),
    fini: (id, total) => majTransfert(id, { fini: true, fait: total }),
    erreur: (id, erreur) => majTransfert(id, { erreur }),
  };

  function telecharger(f) {
    client.telecharger(joindre(chemin, f.nom), observateur)
      .then(({ nom, morceaux }) => enregistrer(nom, morceaux))
      .catch(() => { /* l'erreur d'un transfert commencé est déjà affichée dans la liste */ });
  }

  // Téléversements à la suite, un fichier à la fois : l'écran à distance reste fluide.
  const fileUpload = useRef(Promise.resolve());
  function televerser(fichiers) {
    const dossier = cheminRef.current;
    if (!dossier || fichiers.length === 0) return;
    fileUpload.current = fileUpload.current.then(async () => {
      for (const fichier of fichiers) {
        if (entreesRef.current.some((x) => x.nom === fichier.name && cheminRef.current === dossier)
          && !window.confirm(`« ${fichier.name} » existe déjà dans ce dossier. Le remplacer ?`)) continue;
        try {
          await client.televerser(fichier, dossier, observateur);
        } catch { /* affiché dans la liste des transferts ; on passe au fichier suivant */ }
      }
      if (cheminRef.current === dossier) ouvrir(dossier);
    });
  }

  function nouveauDossier() {
    const nom = prompt('Nom du nouveau dossier')?.trim();
    if (nom) action({ t: 'creer_dossier', chemin: joindre(chemin, nom) });
  }

  function renommer(f) {
    const nom = prompt('Nouveau nom', f.nom)?.trim();
    if (nom && nom !== f.nom) action({ t: 'renommer', chemin: joindre(chemin, f.nom), nouveauNom: nom });
  }

  function supprimer(f) {
    const quoi = f.dossier ? `le dossier « ${f.nom} » et tout son contenu` : `« ${f.nom} »`;
    if (window.confirm(`Supprimer ${quoi} sur le poste de l’usager ?`)) action({ t: 'supprimer', chemin: joindre(chemin, f.nom) });
  }

  function allerA(e) {
    e.preventDefault();
    if (saisie.trim()) ouvrir(saisie.trim());
  }

  function deposer(e) {
    e.preventDefault();
    setSurvol(false);
    if (!chemin) return;
    const fichiers = [];
    let dossiers = 0;
    const items = [...(e.dataTransfer.items || [])];
    items.forEach((it, i) => {
      if (it.kind !== 'file') return;
      if (it.webkitGetAsEntry?.()?.isDirectory) { dossiers += 1; return; }
      const f = e.dataTransfer.files[i];
      if (f) fichiers.push(f);
    });
    if (dossiers > 0) setErreur('Les dossiers ne se téléversent pas : déposez des fichiers.');
    televerser(fichiers);
  }

  // Dossiers d'abord, puis le tri choisi à l'intérieur de chaque groupe.
  const acces = (r, k) => (k === 'nom' ? r.nom.toLowerCase() : k === 'taille' ? r.taille : r.modifie);
  const liste = [...sortRows(entrees.filter((x) => x.dossier), acces), ...sortRows(entrees.filter((x) => !x.dossier), acces)];
  const actifs = transferts.filter((t) => !t.fini && !t.erreur).length;
  const pret = canal.etat === 'ouvert';

  return (
    <div
      className={`${visible ? 'flex' : 'hidden'} flex-col flex-1 min-h-0 relative`}
      onDragOver={(e) => { e.preventDefault(); if (!survol) setSurvol(true); }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setSurvol(false); }}
      onDrop={deposer}
    >
      <div className="flex flex-col gap-2 p-2 border-b border-rule">
        <div className="flex gap-1.5">
          <select
            className="bg-card border border-rule-3 rounded-lg px-2 py-1 text-xs text-fg-2 cursor-pointer max-w-[40%]"
            value=""
            onChange={(e) => e.target.value && ouvrir(e.target.value)}
            aria-label="Dossiers de l’usager et lecteurs"
            disabled={!pret}
          >
            <option value="">Dossiers et lecteurs…</option>
            {racines.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button className={btn} onClick={() => parent && ouvrir(parent)} disabled={!parent} title="Dossier parent">
            <ArrowUp size={13} /> Parent
          </button>
          <button className={btn} onClick={() => ouvrir(chemin)} disabled={!chemin} title="Rafraîchir">
            <RefreshCw size={13} />
          </button>
        </div>
        <form onSubmit={allerA}>
          <input
            className="w-full bg-app border border-rule-3 rounded-lg px-2 py-1 text-xs text-fg font-mono"
            value={saisie}
            onChange={(e) => setSaisie(e.target.value)}
            spellCheck={false}
            aria-label="Chemin du dossier"
            disabled={!pret}
          />
        </form>
        <div className="flex gap-1.5">
          <button className={btn} onClick={nouveauDossier} disabled={!chemin}><FolderPlus size={13} /> Nouveau dossier</button>
          <button className={btn} onClick={() => choix.current?.click()} disabled={!chemin}><Upload size={13} /> Téléverser</button>
          <input ref={choix} type="file" multiple hidden onChange={(e) => { televerser([...e.target.files]); e.target.value = ''; }} />
        </div>
      </div>
      {erreur && <div className="px-3 py-1.5 text-xs text-crit break-words">{erreur}</div>}
      {canal.etat === 'ferme' && <div className="px-3 py-1.5 text-xs text-crit">{canal.raison}</div>}
      <div className="flex-1 min-h-0 overflow-auto">
        <table className="w-full text-xs border-collapse">
          <thead className="sticky top-0 bg-card">
            <tr className="text-left text-muted-2">
              <th className="px-3 py-1.5 font-semibold" {...headerProps('nom')}>Nom{arrow('nom')}</th>
              <th className="px-2 py-1.5 font-semibold text-right" {...headerProps('taille')}>Taille{arrow('taille')}</th>
              <th className="px-2 py-1.5 font-semibold" {...headerProps('modifie')}>Modifié{arrow('modifie')}</th>
              <th />
            </tr>
          </thead>
          <tbody className={chargement ? 'opacity-50' : ''}>
            {liste.length === 0 && (
              <tr><td colSpan={4} className="px-3 py-4 text-muted-2">{chemin ? 'Ce dossier est vide.' : pret ? 'Chargement…' : 'Connexion au poste…'}</td></tr>
            )}
            {liste.map((f) => (
              <tr
                key={f.nom}
                className="border-t border-rule hover:bg-rule/40"
                onDoubleClick={() => (f.dossier ? ouvrir(joindre(chemin, f.nom)) : telecharger(f))}
              >
                <td className="px-3 py-1 max-w-0 w-full">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {f.dossier ? <Folder size={14} className="text-blue-400 shrink-0" /> : <FileIcon size={14} className="text-muted shrink-0" />}
                    {f.dossier ? (
                      <button className="bg-transparent border-none p-0 text-fg cursor-pointer truncate text-left text-xs" onClick={() => ouvrir(joindre(chemin, f.nom))}>{f.nom}</button>
                    ) : (
                      <span className="truncate text-fg" title={f.nom}>{f.nom}</span>
                    )}
                  </div>
                </td>
                <td className="px-2 py-1 text-right text-muted whitespace-nowrap">{f.dossier ? '' : fmtTaille(f.taille)}</td>
                <td className="px-2 py-1 text-muted whitespace-nowrap">{fmtDate(f.modifie)}</td>
                <td className="px-2 py-1 whitespace-nowrap">
                  {!f.dossier && <button className={iconBtn} onClick={() => telecharger(f)} title="Télécharger" aria-label={`Télécharger ${f.nom}`}><Download size={13} /></button>}
                  <button className={iconBtn} onClick={() => renommer(f)} title="Renommer" aria-label={`Renommer ${f.nom}`}><Pencil size={13} /></button>
                  <button className={`${iconBtn} hover:text-crit`} onClick={() => supprimer(f)} title="Supprimer" aria-label={`Supprimer ${f.nom}`}><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {transferts.length > 0 && (
        <div className="border-t border-rule max-h-[35%] overflow-auto">
          <div className="flex items-center justify-between px-3 py-1.5 text-[11px] text-muted-2">
            <span>Transferts · {actifs > 0 ? `${actifs} en cours` : 'terminés'}</span>
            <button className="bg-transparent border-none text-muted cursor-pointer text-[11px]" onClick={() => setTransferts((l) => l.filter((t) => !t.fini && !t.erreur))}>
              Effacer les terminés
            </button>
          </div>
          {transferts.map((t) => (
            <div key={t.id} className="px-3 py-1 text-xs">
              <div className="flex justify-between gap-2">
                <span className="truncate text-fg">{t.sens === 'up' ? '↑' : '↓'} {t.nom}</span>
                <span className={`whitespace-nowrap ${t.erreur ? 'text-crit' : 'text-muted-2'}`}>
                  {t.erreur ? t.erreur : t.fini ? fmtTaille(t.total) : `${fmtTaille(t.fait)} / ${fmtTaille(t.total)}`}
                </span>
              </div>
              <div className="h-1 rounded bg-rule mt-1 overflow-hidden">
                <div className={`h-full ${t.erreur ? 'bg-crit' : 'bg-blue-500'}`} style={{ width: `${t.total ? Math.min(100, (t.fait / t.total) * 100) : t.fini ? 100 : 0}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
      {survol && (
        <div className="absolute inset-0 flex items-center justify-center bg-card/80 border-2 border-dashed border-blue-500 text-sm text-fg pointer-events-none">
          Déposez les fichiers pour les envoyer dans {chemin || 'le dossier courant'}
        </div>
      )}
    </div>
  );
}
